(function (window, document) {
  'use strict';

  if (window.GTenderAuthSheetRuntime) {
    window.GTenderAuthSheetRuntime.boot();
    return;
  }

  const states = new Map();
  let sessionLoad = null;
  const INTENT_STORAGE_KEY = 'gtender:onboarding-intent:v1';
  const INTENT_TTL_MS = 30 * 60 * 1000;
  const CALLBACK_SOURCE = 'gtender-sign-in';
  const ACCESS_REQUEST_TIMEOUT_MS = 10000;
  const VALID_PHASES = new Set([
    'loading',
    'form',
    'sending',
    'sent',
    'verifying',
    'account',
    'error',
  ]);

  const words = (zh, en) =>
    document.documentElement.lang.toLowerCase().startsWith('zh') ? zh : en;

  const normalizeIntent = (value) =>
    String(value || '')
      .trim()
      .toLowerCase() === 'pro'
      ? 'pro'
      : 'free';

  function clearStoredIntent() {
    try {
      window.localStorage.removeItem(INTENT_STORAGE_KEY);
    } catch (_) {}
  }

  function readStoredIntent() {
    try {
      const raw = window.localStorage.getItem(INTENT_STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      const intent = normalizeIntent(parsed && parsed.intent);
      const createdAt = Number(parsed && parsed.created_at);
      const age = Date.now() - createdAt;

      if (
        intent !== 'pro' ||
        !Number.isFinite(createdAt) ||
        createdAt > Date.now() + 60000 ||
        age < 0 ||
        age > INTENT_TTL_MS
      ) {
        clearStoredIntent();
        return null;
      }

      return 'pro';
    } catch (_) {
      clearStoredIntent();
      return null;
    }
  }

  function persistIntent(intent) {
    const normalized = normalizeIntent(intent);
    if (normalized !== 'pro') {
      clearStoredIntent();
      return;
    }

    try {
      window.localStorage.setItem(
        INTENT_STORAGE_KEY,
        JSON.stringify({
          intent: 'pro',
          created_at: Date.now(),
        })
      );
    } catch (_) {}
  }

  function isGtenderCallbackReturn() {
    try {
      return new URL(window.location.href).searchParams.get('source') === CALLBACK_SOURCE;
    } catch (_) {
      return false;
    }
  }

  function consumeCallbackMarker() {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('source') !== CALLBACK_SOURCE) return;
      url.searchParams.delete('source');
      const search = url.searchParams.toString();
      window.history.replaceState(
        window.history.state,
        '',
        url.pathname + (search ? `?${search}` : '')
      );
    } catch (_) {}
  }

  function syncTriggerLabel(button, pending, authenticated) {
    const isFreeEntry = button.hasAttribute('data-gtender-free-entry');

    if (isFreeEntry) {
      button.setAttribute(
        'data-gtender-cta-state',
        pending ? 'checking' : authenticated ? 'account' : 'free'
      );
    }

    const full = pending
      ? words('正在確認 GTender 帳戶', 'Checking GTender account')
      : authenticated
        ? words('GTender 帳戶', 'GTender account')
        : isFreeEntry
          ? words('登入', 'Sign in')
          : words('加入 GTender', 'Join GTender');

    const compact = pending
      ? words('確認中', 'Checking')
      : authenticated
        ? words('帳戶', 'Account')
        : isFreeEntry
          ? words('登入', 'Sign in')
          : words('加入', 'Join');

    const desktop = button.querySelector('.context-bar__account-text');
    const mobile = button.querySelector('.context-bar__gtender-account-mobile-label');

    button.setAttribute('aria-label', full);

    if (desktop || mobile) {
      if (desktop) desktop.textContent = full;
      if (mobile) mobile.textContent = compact;
      return;
    }

    button.textContent = full;
  }

  function ensureSession(sheet) {
    if (window.LittleGWebSession) return Promise.resolve(window.LittleGWebSession);
    if (sessionLoad) return sessionLoad;

    const pending = new Promise((resolve, reject) => {
      const source = sheet.dataset.webSessionSrc;
      if (!source) {
        reject(new Error('Session source missing'));
        return;
      }

      let script = Array.from(document.querySelectorAll('script[src]')).find((node) =>
        /(?:^|\/)littleg-web-session\.js(?:\?|$)/.test(node.src)
      );

      if (script && script.dataset.gtenderSessionLoad === 'failed' && !window.LittleGWebSession) {
        script.remove();
        script = null;
      }

      const created = !script;
      if (created) {
        script = document.createElement('script');
        script.src = source;
        script.dataset.gtenderSessionLoader = 'true';
      }

      let settled = false;
      let timer = null;

      const cleanup = () => {
        window.clearTimeout(timer);
        script.removeEventListener('load', loaded);
        script.removeEventListener('error', failed);
      };

      const finish = (error) => {
        if (settled) return;
        settled = true;
        cleanup();

        if (window.LittleGWebSession) {
          script.dataset.gtenderSessionLoad = 'ready';
          resolve(window.LittleGWebSession);
          return;
        }

        if (created || error.message === 'Session load failed') {
          script.dataset.gtenderSessionLoad = 'failed';
        }
        reject(error);
      };

      const loaded = () => finish(new Error('Session runtime unavailable'));
      const failed = () => finish(new Error('Session load failed'));

      script.addEventListener('load', loaded, { once: true });
      script.addEventListener('error', failed, { once: true });
      timer = window.setTimeout(() => finish(new Error('Session load timed out')), 10000);

      if (created) document.head.appendChild(script);
      if (window.LittleGWebSession) loaded();
    });

    sessionLoad = pending.catch((error) => {
      sessionLoad = null;
      throw error;
    });

    return sessionLoad;
  }

  function mount(sheet) {
    if (states.has(sheet)) return;

    const state = {
      opener: null,
      controller: new AbortController(),
      active: true,
      ready: false,
      sending: false,
      emailSent: false,
      signingOut: false,
      signOutFailed: false,
      callbackReturn: isGtenderCallbackReturn(),
      callbackSettled: false,
      phase: 'loading',
      intent: readStoredIntent() || normalizeIntent(sheet.dataset.onboardingIntent),
    };

    states.set(sheet, state);

    const form = sheet.querySelector('[data-gtender-native-login]');
    const loading = sheet.querySelector('[data-gtender-native-loading]');
    const sent = sheet.querySelector('[data-gtender-native-sent]');
    const errorPanel = sheet.querySelector('[data-gtender-native-error]');
    const errorCopy = sheet.querySelector('[data-gtender-native-error-copy]');
    const account = sheet.querySelector('[data-gtender-native-account]');
    const status = sheet.querySelector('[data-gtender-native-status]');
    const send = sheet.querySelector('[data-gtender-native-send]');
    const retry = sheet.querySelector('[data-gtender-native-retry]');
    const useAnother = sheet.querySelector('[data-gtender-native-use-another]');
    const logout = sheet.querySelector('[data-gtender-native-logout]');
    const dashboard = sheet.querySelector('[data-gtender-native-dashboard]');
    const title = sheet.querySelector('[data-gtender-auth-title]');
    const intro = sheet.querySelector('[data-gtender-auth-intro]');

    if (
      !form ||
      !loading ||
      !sent ||
      !errorPanel ||
      !errorCopy ||
      !account ||
      !status ||
      !send ||
      !retry ||
      !useAnother ||
      !logout ||
      !dashboard ||
      !title ||
      !intro
    ) {
      state.active = false;
      return;
    }

    const signal = state.controller.signal;
    const pics = sheet.querySelector('.gtender-auth-sheet__pics');
    const viewport = window.visualViewport;
    let viewportFrame = 0;

    function updateSheetViewport() {
      viewportFrame = 0;
      if (!state.active || !sheet.isConnected || !sheet.open) return;

      const height = viewport ? viewport.height : window.innerHeight;
      const offsetTop = viewport ? viewport.offsetTop : 0;
      const bottomGap = Math.max(0, window.innerHeight - height - offsetTop);

      sheet.style.setProperty('--gtender-auth-visible-height', `${Math.round(height)}px`);
      sheet.style.setProperty('--gtender-auth-bottom-gap', `${Math.round(bottomGap)}px`);
      sheet.classList.toggle('is-expanded', Boolean(pics && pics.open));

      const focused = document.activeElement;
      if (
        focused instanceof Element &&
        sheet.contains(focused) &&
        focused.matches('input[type="email"]')
      ) {
        const sheetBounds = sheet.getBoundingClientRect();
        const fieldBounds = focused.getBoundingClientRect();

        if (
          fieldBounds.top < sheetBounds.top + 16 ||
          fieldBounds.bottom > sheetBounds.bottom - 16
        ) {
          focused.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'auto',
          });
        }
      }
    }

    function scheduleSheetViewport() {
      if (viewportFrame || !state.active) return;
      viewportFrame = window.requestAnimationFrame(updateSheetViewport);
    }

    if (pics) pics.addEventListener('toggle', scheduleSheetViewport, { signal });
    window.addEventListener('resize', scheduleSheetViewport, { signal });

    sheet.addEventListener(
      'focusin',
      (event) => {
        if (event.target instanceof Element && event.target.matches('input[type="email"]')) {
          scheduleSheetViewport();
        }
      },
      { signal }
    );

    if (viewport) {
      viewport.addEventListener('resize', scheduleSheetViewport, { signal });
      viewport.addEventListener('scroll', scheduleSheetViewport, { signal });
    }

    const sheetOpenObserver = new MutationObserver(scheduleSheetViewport);
    sheetOpenObserver.observe(sheet, { attributes: true, attributeFilter: ['open'] });

    sheet.addEventListener(
      'close',
      () => {
        window.cancelAnimationFrame(viewportFrame);
        viewportFrame = 0;
        sheet.style.removeProperty('--gtender-auth-visible-height');
        sheet.style.removeProperty('--gtender-auth-bottom-gap');
      },
      { signal }
    );

    signal.addEventListener(
      'abort',
      () => {
        sheetOpenObserver.disconnect();
        window.cancelAnimationFrame(viewportFrame);
        viewportFrame = 0;
        sheet.classList.remove('is-expanded');
        sheet.style.removeProperty('--gtender-auth-visible-height');
        sheet.style.removeProperty('--gtender-auth-bottom-gap');
      },
      { once: true }
    );

    function message(value, tone = 'neutral') {
      if (!state.active || !sheet.isConnected) return;
      status.textContent = value || '';
      status.dataset.tone = tone;
    }

    function applyIntent(intent, explicit) {
      state.intent = normalizeIntent(intent);
      sheet.dataset.onboardingIntent = state.intent;

      if (explicit && state.intent === 'free') clearStoredIntent();

      if (state.intent === 'pro') {
        title.textContent = words('開始使用 GTender Pro', 'Start with GTender Pro');
        intro.textContent = words(
          '先建立或登入同一個 GTender 帳戶；登入後我們會由後端確認你目前的方案。',
          'Create or sign in to the same GTender account first. After sign-in, your current plan is verified by the backend.'
        );
        dashboard.textContent = words('繼續使用 GTender Pro', 'Continue with GTender Pro');
      } else {
        title.textContent = /^zh-CN$/i.test(String(document.documentElement.lang || '').trim())
          ? '登录或创建账户'
          : words('登入或建立帳戶', 'Sign in or create an account');
        intro.textContent = /^zh-CN$/i.test(String(document.documentElement.lang || '').trim())
          ? '输入 Email，免费开始使用 GTender。已有账户可直接登录。'
          : words(
              '輸入 Email，免費開始使用 GTender。已有帳戶可直接登入。',
              'Enter your email to get started for free. If you already have an account, we’ll sign you in.'
            );
        dashboard.textContent = words('繼續到 GTender', 'Continue to GTender');
      }
    }

    function setStepState(name, value) {
      const step = sheet.querySelector(`[data-gtender-auth-step="${name}"]`);
      if (step) step.dataset.stepState = value;
    }

    function focusEmailWhenReady() {
      const email = form.elements.email;
      if (!email || !sheet.open || state.phase !== 'form') return;

      const active = document.activeElement;
      if (
        active &&
        active !== document.body &&
        active !== sheet &&
        sheet.contains(active) &&
        !active.hasAttribute('data-gtender-auth-close')
      )
        return;

      window.setTimeout(() => {
        if (!state.active || !sheet.open || state.phase !== 'form') return;
        const current = document.activeElement;
        if (
          current &&
          current !== document.body &&
          current !== sheet &&
          sheet.contains(current) &&
          !current.hasAttribute('data-gtender-auth-close')
        )
          return;
        try {
          email.focus({ preventScroll: true });
        } catch (_) {
          try {
            email.focus();
          } catch (_) {}
        }
      }, 0);
    }

    function setPhase(next) {
      const phase = VALID_PHASES.has(next) ? next : 'form';
      state.phase = phase;
      sheet.dataset.authPhase = phase;

      loading.hidden = phase !== 'loading' && phase !== 'verifying';
      form.hidden = phase !== 'form' && phase !== 'sending';
      sent.hidden = phase !== 'sent';
      errorPanel.hidden = phase !== 'error';
      account.hidden = phase !== 'account';

      send.disabled = phase === 'sending';
      logout.disabled = state.signingOut;

      if (phase === 'form' || phase === 'loading' || phase === 'sending') {
        setStepState('email', phase === 'loading' ? 'pending' : 'active');
        setStepState('verify', 'pending');
        setStepState('workspace', 'pending');
      } else if (phase === 'sent' || phase === 'verifying') {
        setStepState('email', 'done');
        setStepState('verify', 'active');
        setStepState('workspace', 'pending');
      } else if (phase === 'error') {
        setStepState('email', 'done');
        setStepState('verify', 'error');
        setStepState('workspace', 'pending');
      } else if (phase === 'account') {
        setStepState('email', 'done');
        setStepState('verify', 'done');
        setStepState('workspace', 'active');
      }

      if (phase === 'form') focusEmailWhenReady();
    }

    function callbackErrorMessage(code) {
      const normalized = String(code || '')
        .trim()
        .toLowerCase();
      if (normalized === 'session_expired') {
        return words(
          '這個登入連結已過期。請重新取得一個新的登入連結。',
          'This sign-in link has expired. Get a new sign-in link to continue.'
        );
      }
      if (
        normalized === 'unauthorized' ||
        normalized === 'invalid_assertion' ||
        normalized === 'exchange_failed'
      ) {
        return words(
          '這個登入連結無法再使用。請重新取得一個新的登入連結。',
          'This sign-in link can no longer be used. Get a new sign-in link to continue.'
        );
      }
      return words(
        '無法登入。請重新取得一個新的登入連結；公開招標仍可使用。',
        "Couldn't sign in. Get a new sign-in link; public tenders remain available."
      );
    }

    function render() {
      if (!state.active) return;

      const session = window.LittleGWebSession;
      const current = session && session.getState ? session.getState() : null;
      const statusName = current && String(current.status || '').toLowerCase();
      const authenticated = statusName === 'authenticated';
      const pending = !state.ready || statusName === 'booting' || statusName === 'authenticating';
      const signOutPending = state.signingOut || state.signOutFailed;

      if (signOutPending) {
        setPhase('account');
      } else if (authenticated) {
        state.emailSent = false;
        setPhase('account');
      } else if (state.callbackReturn && pending) {
        setPhase('verifying');
      } else if (
        state.callbackReturn &&
        state.ready &&
        (statusName === 'error' || statusName === 'guest' || statusName === 'anonymous')
      ) {
        errorCopy.textContent = callbackErrorMessage(current && current.error);
        setPhase('error');
      } else if (state.emailSent) {
        setPhase('sent');
      } else if (pending) {
        setPhase('loading');
      } else {
        setPhase('form');
      }

      const profileLine = sheet.querySelector('[data-gtender-native-profile]');
      if (profileLine) {
        const email =
          authenticated && current.profile && typeof current.profile.email === 'string'
            ? current.profile.email.trim()
            : '';
        profileLine.hidden = !email;
        profileLine.textContent = email ? words('目前登入：', 'Signed in as: ') + email : '';
      }

      logout.textContent = state.signingOut
        ? words('正在登出…', 'Signing out…')
        : state.signOutFailed
          ? words('重試登出', 'Retry sign-out')
          : words('登出', 'Sign out');

      document.querySelectorAll('[data-gtender-native-auth-open]').forEach((button) => {
        syncTriggerLabel(button, pending, authenticated);
        if (button.hasAttribute('data-gtender-primary-auth')) button.hidden = false;
      });

      document
        .querySelectorAll('[data-context-product="gtender"][data-gtender-surface="public"]')
        .forEach((header) => {
          const workspace = header.querySelector('[data-gtender-workspace-entry]');
          if (!workspace) return;
          const visible = !pending && !signOutPending && authenticated;
          workspace.setAttribute('data-gtender-session-visible', String(visible));
          workspace.hidden = !visible;
        });
    }

    function finalizeClose() {
      document.documentElement.classList.remove('gtender-auth-open');
      if (form.elements.email) form.elements.email.value = '';
      if (pics) pics.open = false;
      const opener = state.opener;
      state.opener = null;
      if (opener && opener.isConnected && typeof opener.focus === 'function') {
        window.setTimeout(() => {
          try {
            opener.focus({ preventScroll: true });
          } catch (_) {
            try {
              opener.focus();
            } catch (_) {}
          }
        }, 0);
      }
    }

    function close() {
      let nativeClose = false;
      try {
        if (typeof sheet.close === 'function' && sheet.open) {
          nativeClose = true;
          sheet.close();
        } else {
          sheet.removeAttribute('open');
        }
      } catch (_) {
        sheet.removeAttribute('open');
      }
      if (!nativeClose) finalizeClose();
    }

    function focusableControls() {
      return Array.from(
        sheet.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((node) => !node.hidden && node.getClientRects().length > 0);
    }

    function localDestination(raw, expected) {
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin || !expected(url.pathname)) {
        throw new Error('Invalid GTender destination');
      }
      return url;
    }

    async function destinationForIntent(session) {
      const dashboardUrl = localDestination(sheet.dataset.dashboardUrl, (pathname) =>
        pathname.replace(/\/+$/, '').endsWith('/pages/gtender-dashboard')
      );

      if (state.intent !== 'pro') return dashboardUrl;

      const accessUrl = new URL(sheet.dataset.accessUrl || '', window.location.origin);
      if (
        accessUrl.origin !== 'https://api.glhksolution.com' ||
        accessUrl.pathname !== '/v1/gtender/access'
      ) {
        throw new Error('Invalid GTender access endpoint');
      }

      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const options = {
        method: 'GET',
        headers: { Accept: 'application/json' },
      };
      if (controller) options.signal = controller.signal;

      let timeout = null;
      const abortAccess = () => {
        if (controller) controller.abort();
      };
      if (controller) signal.addEventListener('abort', abortAccess, { once: true });

      const deadline = new Promise((_, reject) => {
        timeout = window.setTimeout(() => {
          abortAccess();
          const error = new Error('GTender Pro access verification timed out.');
          error.code = 'gtender_access_timeout';
          reject(error);
        }, ACCESS_REQUEST_TIMEOUT_MS);
      });

      let response;
      try {
        response = await Promise.race([
          session.authenticatedFetch(accessUrl.href, options),
          deadline,
        ]);
      } finally {
        if (timeout !== null) window.clearTimeout(timeout);
        if (controller) signal.removeEventListener('abort', abortAccess);
      }

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload.success !== true || !payload.data) {
        throw new Error(
          (payload && payload.error && payload.error.code) || 'gtender_access_unavailable'
        );
      }

      if (payload.data.mode === 'pro' && payload.data.pro_entitled === true) return dashboardUrl;

      const proUrl = new URL(sheet.dataset.proUrl || '', window.location.origin);
      if (
        proUrl.origin !== window.location.origin ||
        !proUrl.pathname.replace(/\/+$/, '').endsWith('/products/gtender-pro')
      ) {
        throw new Error('Invalid GTender Pro destination');
      }

      return localDestination(proUrl.href, (pathname) => pathname === proUrl.pathname);
    }

    async function prepare() {
      if (state.signingOut || state.signOutFailed) {
        render();
        message(
          state.signingOut
            ? words('正在登出…', 'Signing out…')
            : words(
                '未能完成登出，請按「重試登出」。',
                'Couldn’t finish signing out. Select “Retry sign-out”.'
              ),
          state.signOutFailed ? 'error' : 'neutral'
        );
        return;
      }

      if (state.callbackReturn) {
        setPhase('verifying');
        message(words('正在驗證 Email 登入連結…', 'Verifying your email sign-in link…'));
      } else {
        message(words('正在確認產品帳戶…', 'Checking product account…'));
      }

      try {
        const session = await ensureSession(sheet);
        await session.ready();

        const readyState = session.getState();
        if (!state.callbackReturn || readyState.status !== 'error') {
          await session.restore();
        }

        if (!state.active) return;

        state.ready = true;
        render();

        const current = session.getState();
        if (state.callbackReturn) {
          if (current.status === 'authenticated') {
            state.callbackSettled = true;
            consumeCallbackMarker();
            message(
              words(
                'Email 驗證完成。你的 GTender 帳戶已登入。',
                'Email verified. Your GTender account is signed in.'
              ),
              'success'
            );
          } else if (
            current.status === 'error' ||
            current.status === 'guest' ||
            current.status === 'anonymous'
          ) {
            state.callbackSettled = true;
            consumeCallbackMarker();
            errorCopy.textContent = callbackErrorMessage(current.error);
            setPhase('error');
            message(errorCopy.textContent, 'error');
          }
        } else if (current.status === 'error') {
          message(
            words(
              '暫時無法確認登入狀態。你仍可瀏覽公開招標或重新登入。',
              'Sign-in state could not be confirmed. Public browsing remains available.'
            ),
            'error'
          );
        } else if (!state.emailSent) {
          message('');
        }
      } catch (_) {
        if (!state.active) return;
        state.ready = false;
        errorCopy.textContent = words(
          '產品登入暫時無法載入。請稍後重試；公開搜尋不受影響。',
          'Product sign-in could not load. Try again shortly; public search remains available.'
        );
        setPhase('error');
        message(errorCopy.textContent, 'error');
      }
    }

    function revalidateOnResume() {
      if (!state.active || document.visibilityState === 'hidden') return;
      const persisted = readStoredIntent();
      if (persisted) applyIntent(persisted, false);
      if (sheet.open || persisted || state.callbackReturn) void prepare();
    }

    function openSheet(opener) {
      const mobileMenuEntry = Boolean(
        opener &&
        opener.closest('[data-gtender-mobile-tools-ready]') &&
        window.matchMedia('(max-width: 767px)').matches
      );

      state.opener = mobileMenuEntry
        ? document.querySelector('[data-sidebar-launcher="mobile"]')
        : opener || null;
      try {
        if (typeof sheet.showModal === 'function') {
          if (!sheet.open) sheet.showModal();
        } else {
          sheet.setAttribute('open', '');
        }
      } catch (_) {
        sheet.setAttribute('open', '');
      }
      if (!sheet.open && !sheet.hasAttribute('open')) return false;
      document.documentElement.classList.add('gtender-auth-open');
      scheduleSheetViewport();
      if (state.phase === 'form') focusEmailWhenReady();
      return true;
    }

    document.addEventListener(
      'click',
      (event) => {
        const targetNode = event.target;
        if (!(targetNode instanceof Element)) return;

        const trigger = targetNode.closest(
          '[data-gtender-auth-open], [data-gtender-native-auth-open]'
        );
        if (!trigger) return;

        const targetId = trigger.getAttribute('data-gtender-auth-open');
        if (targetId && targetId !== sheet.id) return;
        if (!targetId && sheet !== document.querySelector('[data-gtender-auth-sheet]')) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          return;
        if (!openSheet(trigger)) return;

        event.preventDefault();
        state.emailSent = false;
        applyIntent(trigger.getAttribute('data-gtender-plan-intent') || 'free', true);
        void prepare();
      },
      { signal }
    );

    form.addEventListener(
      'submit',
      async (event) => {
        event.preventDefault();
        if (state.sending || state.signingOut || state.signOutFailed || !form.reportValidity())
          return;

        const session = window.LittleGWebSession;
        if (!session || !state.ready) {
          message(
            words('登入尚未準備好，請稍後再試。', 'Sign-in is not ready. Try again shortly.'),
            'error'
          );
          return;
        }

        state.sending = true;
        setPhase('sending');
        persistIntent(state.intent);
        message(words('正在寄出安全登入連結…', 'Sending your secure sign-in link…'));

        try {
          await session.requestMagicLink(form.elements.email.value, { client: 'gtender_web' });
          if (!state.active) return;

          state.emailSent = true;
          setPhase('sent');
          message(
            words(
              '請查看 Email。完成 Email 驗證後會回到 GTender 工作區。',
              'Check your email. Complete email verification to return to your GTender workspace.'
            ),
            'success'
          );
        } catch (_) {
          clearStoredIntent();
          state.emailSent = false;
          setPhase('form');
          message(
            words(
              '未能繼續 Email 登入，請稍後再試。',
              'Email sign-in could not continue. Try again later.'
            ),
            'error'
          );
        } finally {
          state.sending = false;
          if (state.active) render();
        }
      },
      { signal }
    );

    useAnother.addEventListener(
      'click',
      () => {
        state.emailSent = false;
        state.callbackReturn = false;
        state.callbackSettled = true;
        consumeCallbackMarker();
        setPhase('form');
        message('');
        const email = form.elements.email;
        if (email) {
          email.value = '';
          email.focus({ preventScroll: true });
        }
      },
      { signal }
    );

    retry.addEventListener(
      'click',
      () => {
        state.emailSent = false;
        state.callbackReturn = false;
        state.callbackSettled = true;
        consumeCallbackMarker();
        setPhase('form');
        message(
          words('請輸入 Email 取得新的登入連結。', 'Enter your email to Get a new sign-in link.')
        );
        const email = form.elements.email;
        if (email) email.focus({ preventScroll: true });
      },
      { signal }
    );

    logout.addEventListener(
      'click',
      async () => {
        const session = window.LittleGWebSession;
        if (!session || state.signingOut) return;

        state.signingOut = true;
        state.signOutFailed = false;
        render();
        message(words('正在登出…', 'Signing out…'));

        try {
          await session.logout();
          if (!state.active) return;

          clearStoredIntent();
          state.emailSent = false;
          state.callbackReturn = false;
          applyIntent('free', false);
          if (form.elements.email) form.elements.email.value = '';
          message(
            words(
              '已登出。請用另一個 Email 繼續。',
              'Signed out. You can continue with another email.'
            ),
            'success'
          );
        } catch (_) {
          if (!state.active) return;
          state.signOutFailed = true;
          message(
            words(
              '未能完成登出，請按「重試登出」。',
              'Couldn’t finish signing out. Select “Retry sign-out”.'
            ),
            'error'
          );
        } finally {
          state.signingOut = false;
          if (state.active) render();
        }
      },
      { signal }
    );

    dashboard.addEventListener(
      'click',
      async () => {
        if (state.signingOut || state.signOutFailed) return;

        const session = window.LittleGWebSession;
        if (!session || session.getState().status !== 'authenticated') {
          render();
          return;
        }

        dashboard.disabled = true;
        message(
          state.intent === 'pro'
            ? words('正在確認 GTender Pro 方案…', 'Checking your GTender Pro plan…')
            : words('正在開啟 GTender 工作區…', 'Opening your GTender workspace…')
        );

        try {
          const destination = await destinationForIntent(session);
          clearStoredIntent();
          window.location.assign(destination.href);
        } catch (_) {
          dashboard.disabled = false;
          message(
            words(
              '暫時無法確認 GTender 方案。請稍後再試；公開招標仍可使用。',
              'Your GTender plan could not be verified. Try again later; public tenders remain available.'
            ),
            'error'
          );
        }
      },
      { signal }
    );

    document.addEventListener(
      'littleg:web-session-change',
      () => {
        render();
        const session = window.LittleGWebSession;
        const current = session && session.getState();

        if (state.callbackReturn && current) {
          if (current.status === 'authenticating') {
            setPhase('verifying');
            message(words('正在驗證 Email 登入連結…', 'Verifying your email sign-in link…'));
          } else if (current.status === 'authenticated') {
            state.callbackSettled = true;
            consumeCallbackMarker();
            setPhase('account');
            message(
              words(
                'Email 驗證完成。你的 GTender 帳戶已登入。',
                'Email verified. Your GTender account is signed in.'
              ),
              'success'
            );
          } else if (
            state.ready &&
            (current.status === 'error' ||
              current.status === 'guest' ||
              current.status === 'anonymous')
          ) {
            state.callbackSettled = true;
            consumeCallbackMarker();
            errorCopy.textContent = callbackErrorMessage(current.error);
            setPhase('error');
            message(errorCopy.textContent, 'error');
          }
        }

        if (current && current.status === 'guest' && !state.callbackReturn && !state.emailSent) {
          if (form.elements.email && !state.signingOut) form.elements.email.value = '';
        }
      },
      { signal }
    );

    window.addEventListener('focus', revalidateOnResume, { signal });
    window.addEventListener('pageshow', revalidateOnResume, { signal });

    sheet.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          return;
        }
        if (event.key !== 'Tab') return;

        const controls = focusableControls();
        if (!controls.length) return;

        const first = controls[0];
        const last = controls[controls.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },
      { signal }
    );

    sheet.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        close();
      },
      { signal }
    );

    sheet.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('[data-gtender-auth-close]')) close();

        if (event.target === sheet) {
          const box = sheet.getBoundingClientRect();
          if (
            event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom
          )
            close();
        }
      },
      { signal }
    );

    sheet.addEventListener(
      'close',
      () => {
        finalizeClose();
      },
      { signal }
    );

    applyIntent(state.intent, false);
    render();

    if (state.callbackReturn && openSheet(null)) {
      setPhase('verifying');
      message(words('正在驗證 Email 登入連結…', 'Verifying your email sign-in link…'));
    }

    void prepare();
  }

  function boot() {
    document.querySelectorAll('[data-gtender-auth-sheet]').forEach(mount);
  }

  function destroy(scope) {
    states.forEach((state, sheet) => {
      if (scope === document || scope.contains(sheet)) {
        if (sheet.open) sheet.close();
        state.active = false;
        state.controller.abort();
        states.delete(sheet);
      }
    });
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));

  window.GTenderAuthSheetRuntime = Object.freeze({ boot, destroy });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})(window, document);
